import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SpeechSegment, TaggedReplica } from '../../../../services/api.service';
import {
  findActiveSegmentIndex,
  formatSecondsShort,
  getSegmentEndSec,
  getSegmentStartSec,
  getSpeakerName,
} from '../../utils/audio-segments';
import { formatSpeakerWithLang } from '../../utils/speaker-language';
import {
  MARKER_COLORS,
  MARKER_LABELS,
  MomentMarkerType,
  segmentMatchesMomentFilter,
} from '../../utils/video-moments.utils';
import styles from './video-analytics.module.scss';

type FilterKey = 'all' | MomentMarkerType;

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'Все' },
  { key: 'dispute', label: 'спор' },
  { key: 'KPI', label: 'KPI' },
  { key: 'decision', label: 'решение' },
  { key: 'risk', label: 'риск' },
  { key: 'task', label: 'задача' },
];

type Props = {
  segments: SpeechSegment[];
  taggedReplicas?: TaggedReplica[];
  currentTime: number;
  isPlaying?: boolean;
  onSeek: (seconds: number) => void;
  onTogglePlayPause?: () => void;
};

function speakerInitials(name: string): string {
  const parts = name.replace(/\s*\([^)]*\)/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function primaryTag(tags: string[]): MomentMarkerType | null {
  const upper = tags.map(t => t.toUpperCase());
  if (upper.some(t => t === 'CONFLICT' || t === 'DISPUTE')) return 'dispute';
  if (upper.includes('DECISION')) return 'decision';
  if (upper.includes('RISK')) return 'risk';
  if (upper.includes('TASK')) return 'task';
  if (upper.includes('KPI')) return 'KPI';
  return null;
}

const SyncedTranscript: React.FC<Props> = ({
  segments,
  taggedReplicas = [],
  currentTime,
  isPlaying = false,
  onSeek,
  onTogglePlayPause,
}) => {
  const [filter, setFilter] = useState<FilterKey>('all');
  const rowRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const listRef = useRef<HTMLDivElement | null>(null);

  const tagBySecond = useMemo(() => {
    const map = new Map<number, string[]>();
    taggedReplicas.forEach(tr => {
      const sec = Math.round(tr.timestamp_sec || 0);
      const existing = map.get(sec) || [];
      tr.tags.forEach(t => {
        if (!existing.includes(t)) existing.push(t);
      });
      map.set(sec, existing);
    });
    return map;
  }, [taggedReplicas]);

  const findTags = (startSec: number): string[] => {
    const rounded = Math.round(startSec);
    for (const delta of [0, 1, -1, 2, -2, 3, -3]) {
      const tags = tagBySecond.get(rounded + delta);
      if (tags?.length) return tags;
    }
    return [];
  };

  const rows = useMemo(() => {
    return segments
      .map(seg => {
        const startSec = getSegmentStartSec(seg);
        const endSec = getSegmentEndSec(seg);
        const base = getSpeakerName(seg);
        const speaker = formatSpeakerWithLang(base, seg.speaker_language);
        const isKz = seg.speaker_language === 'kz' || !!seg.text_kk?.trim();
        const text = (isKz ? (seg.text_kk || seg.text_ru || seg.text) : (seg.text_ru || seg.text))?.trim() || '';
        const tags = findTags(startSec);
        return { seg, startSec, endSec, speaker, text, tags };
      })
      .filter(r => r.text)
      .sort((a, b) => a.startSec - b.startSec);
  }, [segments, tagBySecond]);

  const filteredRows = useMemo(() => {
    if (filter === 'all') return rows;
    return rows.filter(r => segmentMatchesMomentFilter(r.tags, filter));
  }, [rows, filter]);

  const activeIndex = useMemo(() => {
    const idx = findActiveSegmentIndex(rows, currentTime);
    if (idx < 0) return -1;
    const startSec = rows[idx].startSec;
    return filteredRows.findIndex(r => r.startSec === startSec);
  }, [filteredRows, rows, currentTime]);

  useEffect(() => {
    if (!isPlaying || activeIndex < 0) return;
    const el = rowRefs.current.get(activeIndex);
    if (!el || !listRef.current) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeIndex, isPlaying]);

  const handleRowClick = (row: (typeof filteredRows)[number], isActive: boolean) => {
    if (isActive) {
      onTogglePlayPause?.();
      return;
    }
    onSeek(row.startSec);
  };

  if (!rows.length) {
    return <p className={styles.syncedTranscriptEmpty}>Транскрипт пуст.</p>;
  }

  return (
    <div className={styles.syncedTranscript}>
      <div className={styles.syncedTranscriptHeader}>
        <span className={styles.syncedTranscriptTitle}>Транскрипт синхронизированный по видео</span>
        <div className={styles.syncedTranscriptFilters}>
          {FILTERS.map(f => (
            <button
              key={f.key}
              type="button"
              className={`${styles.syncedFilterBtn} ${filter === f.key ? styles.syncedFilterBtnActive : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.key !== 'all' && (
                <span
                  className={styles.syncedFilterDot}
                  style={{ backgroundColor: MARKER_COLORS[f.key] }}
                />
              )}
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.syncedTranscriptList} ref={listRef}>
        {filteredRows.map((row, i) => {
          const isActive = i === activeIndex;
          const tag = primaryTag(row.tags);
          const displayTag = tag ? MARKER_LABELS[tag] : null;
          return (
            <div
              key={row.seg.id ?? i}
              ref={el => {
                if (el) rowRefs.current.set(i, el);
                else rowRefs.current.delete(i);
              }}
              className={`${styles.syncedTranscriptRow} ${isActive ? styles.syncedTranscriptRowActive : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => handleRowClick(row, isActive)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleRowClick(row, isActive);
                }
              }}
            >
              <span className={styles.syncedTranscriptTime}>
                {formatSecondsShort(row.startSec)}
              </span>
              <span className={styles.syncedTranscriptAvatar}>
                {speakerInitials(row.speaker)}
              </span>
              <div className={styles.syncedTranscriptContent}>
                <span className={styles.syncedTranscriptSpeaker}>{row.speaker}</span>
                <p className={styles.syncedTranscriptText}>{row.text}</p>
              </div>
              {displayTag && (
                <span
                  className={styles.syncedTranscriptTag}
                  style={{
                    color: MARKER_COLORS[tag!],
                    borderColor: MARKER_COLORS[tag!],
                  }}
                >
                  {displayTag}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SyncedTranscript;
